import React, { useState, useEffect, useRef } from 'react';
import './ConversationView.css';

const MAX_MESSAGE_LENGTH = 2000;

const formatLetterDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

/**
 * Individual letter thread view — envelope-style design
 * @param {Object} props
 * @param {Array} props.messages - Messages in the conversation
 * @param {Object} props.connection - Connection info
 * @param {string} props.otherHandle - The other person's handle
 * @param {boolean} props.loading - Whether messages are loading
 * @param {boolean} props.sending - Whether a message is being sent
 * @param {number} props.totalMessages - User's total available messages
 * @param {Function} props.onSend - Handler when message is sent
 * @param {Function} props.onBack - Handler to go back to inbox
 * @param {Function} props.onBuyMessages - Handler to open buy messages modal
 */
export default function ConversationView({
  messages = [],
  connection,
  otherHandle,
  loading,
  sending,
  totalMessages = 0,
  onSend,
  onBack,
  onBuyMessages,
}) {
  const [newMessage, setNewMessage] = useState('');
  const [error, setError] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;

    if (newMessage.length > MAX_MESSAGE_LENGTH) {
      setError(`Message too long (max ${MAX_MESSAGE_LENGTH} characters)`);
      return;
    }

    if (totalMessages <= 0) {
      setError('No letters available');
      return;
    }

    setError(null);

    try {
      const result = await onSend(newMessage.trim());
      if (result.success) {
        setNewMessage('');
      } else {
        setError(result.error || 'Failed to send');
      }
    } catch (err) {
      setError(err.message || 'Failed to send letter');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  const remainingChars = MAX_MESSAGE_LENGTH - newMessage.length;
  const isOverLimit = remainingChars < 0;
  const canSend = newMessage.trim().length > 0 && !isOverLimit && totalMessages > 0 && !sending;

  return (
    <div className="conversation-view">
      {/* Header */}
      <div className="conversation-header">
        <button className="conversation-back" onClick={onBack} aria-label="Back">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="conversation-title">
          <span className="conversation-handle">Letters with @{otherHandle}</span>
        </div>
        <div className="conversation-spacer"></div>
      </div>

      {/* Messages */}
      <div className="conversation-messages">
        {loading ? (
          <div className="conversation-loading">
            <div className="loading-spinner"></div>
            <span>Loading letters...</span>
          </div>
        ) : messages.length === 0 ? (
          <div className="conversation-empty">
            <span className="empty-icon">&#128140;</span>
            <span>No letters yet</span>
          </div>
        ) : (
          <>
            {messages.map((msg, index) => {
              const isLast = index === messages.length - 1;
              const isExpanded = isLast || expandedIds.has(msg.id);

              return (
                <React.Fragment key={msg.id}>
                  {isExpanded ? (
                    <div className={`letter-card ${!isLast ? 'letter-card-clickable' : ''}`} onClick={!isLast ? () => toggleExpand(msg.id) : undefined}>
                      <div className="letter-card-envelope-edge"></div>
                      <div className="letter-card-inner">
                        <div className="letter-card-header">
                          <span className="letter-card-from">
                            {msg.is_mine ? 'From you' : `From @${otherHandle}`}
                          </span>
                          <span className="letter-card-date">
                            {formatLetterDate(msg.created_at)}
                          </span>
                        </div>
                        <div className="letter-card-body">
                          {msg.content}
                        </div>
                        {msg.is_mine && msg.read_at && (
                          <div className="letter-card-footer">
                            <span className="letter-read-receipt" title={`Read ${formatLetterDate(msg.read_at)}`}>
                              Read
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <button className="letter-compact" onClick={() => toggleExpand(msg.id)}>
                      <span className="letter-compact-from">
                        {msg.is_mine ? 'You' : `@${otherHandle}`}
                      </span>
                      <span className="letter-compact-preview">
                        {msg.content.length > 80 ? msg.content.slice(0, 80).trim() + '...' : msg.content}
                      </span>
                      <span className="letter-compact-date">
                        {formatLetterDate(msg.created_at)}
                      </span>
                    </button>
                  )}
                  {index < messages.length - 1 && isExpanded && (
                    <div className="letter-divider">
                      <span className="letter-divider-ornament">&#10047;</span>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Compose Area */}
      <div className="conversation-compose">
        {error && (
          <div className="compose-error">{error}</div>
        )}

        <div className="compose-letter-paper">
          <textarea
            ref={textareaRef}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder=""
            className="compose-letter-textarea"
            disabled={sending}
            rows={4}
          />
          {newMessage.length > 0 && (
            <span className={`compose-char-count ${isOverLimit ? 'over' : remainingChars < 200 ? 'warn' : ''}`}>
              {remainingChars}
            </span>
          )}
        </div>

        <div className="compose-actions">
          <div className="compose-messages-count">
            {totalMessages > 0 ? (
              <span>
                <span className="messages-envelope-inline" aria-hidden>
                  📧
                </span>
                {totalMessages} letter{totalMessages !== 1 ? 's' : ''} remaining for today
              </span>
            ) : (
              <button className="buy-messages-link" onClick={onBuyMessages}>
                No letters remaining - Get more
              </button>
            )}
          </div>
          <button
            className="send-letter-button"
            onClick={handleSend}
            disabled={!canSend}
          >
            {sending ? (
              <span className="send-spinner"></span>
            ) : (
              'Send Letter'
            )}
          </button>
        </div>

        <div className="compose-hint">
          Press {navigator.platform?.includes('Mac') ? 'Cmd' : 'Ctrl'}+Enter to send
        </div>
      </div>
    </div>
  );
}
